import React, { useState } from "react";
import { FaInfoCircle } from "react-icons/fa";
import {
  DEFAULT_RATIO_DENOMINATOR,
  DEFAULT_RATIO_NUMERATOR,
  EASY_PLACES,
  PAYOUT_SHAPES,
  firstCapStartsAtPlaces,
  isPayoutPlanningPartial,
} from "../../../utility/generateShapedPayoutTable";
import PayoutCurveChart from "./PayoutCurveChart";
import {
  PrimaryButton,
  SecondaryButton,
  ToggleSwitch,
} from "../../UI/DesignStandards";

const inputClass =
  "w-full px-3 py-2 border border-brand-charcoal/20 rounded-lg bg-white text-brand-charcoal focus:outline-none focus:ring-2 focus:ring-brand-gold-ink";

const dollarWrapClass =
  "flex items-center gap-1 px-3 py-2 border border-brand-charcoal/20 rounded-lg bg-white focus-within:ring-2 focus-within:ring-brand-gold-ink";

const formatButtonClass =
  "px-2.5 py-1.5 text-sm border border-brand-charcoal/20 rounded-lg bg-white text-brand-charcoal hover:border-brand-gold-ink disabled:opacity-50 disabled:cursor-not-allowed";

export const RoundWholeDollarsSwitch = ({
  checked,
  onChange,
  disabled = false,
}) => (
  <label
    className={`flex items-center gap-3 ${disabled ? "" : "cursor-pointer"}`}
  >
    <ToggleSwitch
      checked={checked}
      onChange={onChange}
      disabled={disabled}
      className="shrink-0"
    />
    <span
      className={`text-sm font-medium ${
        disabled ? "text-brand-muted" : "text-brand-charcoal"
      }`}
    >
      Round place amounts to whole dollars
    </span>
  </label>
);

const ExportFormats = ({
  label,
  scope,
  onExport,
  disabled = false,
  exportingKey = "",
}) => (
  <div>
    <p className="text-xs font-medium text-brand-muted mb-1">{label}</p>
    <div className="flex flex-wrap gap-2">
      {["csv", "png"].map((format) => {
        const key = `${scope}-${format}`;
        const busy = exportingKey === key;
        return (
          <button
            key={format}
            type="button"
            className={formatButtonClass}
            disabled={disabled || Boolean(exportingKey)}
            onClick={() => onExport(scope, format)}
          >
            {busy ? `${format.toUpperCase()}...` : format.toUpperCase()}
          </button>
        );
      })}
    </div>
  </div>
);

const DollarInput = ({
  id,
  label,
  value,
  disabled,
  onChange,
  wholeDollars = false,
}) => (
  <div>
    <label
      htmlFor={id}
      className="block text-sm font-medium text-brand-charcoal mb-1"
    >
      {label}
    </label>
    <div className={`${dollarWrapClass} ${disabled ? "opacity-60" : ""}`}>
      <span className="text-brand-muted font-score">$</span>
      <input
        id={id}
        type="number"
        min={0}
        step={wholeDollars ? "1" : "0.01"}
        value={value}
        disabled={disabled}
        onChange={(e) => {
          const raw = e.target.value;
          if (!wholeDollars) {
            onChange(raw);
            return;
          }
          if (raw === "") {
            onChange("");
            return;
          }
          if (/^\d+$/.test(raw)) {
            onChange(raw);
            return;
          }
          const n = Number(raw);
          if (Number.isFinite(n) && n >= 0) {
            onChange(n === 0 ? "" : String(Math.round(n)));
          }
        }}
        className="w-full border-none focus:outline-none bg-transparent font-score text-brand-charcoal"
      />
    </div>
  </div>
);

const PayoutOptionsPanel = ({
  canEdit,
  showMore,
  onToggleMore,
  places,
  shape,
  ratioNumerator,
  ratioDenominator,
  payoutData = [],
  planning,
  onPlanningChange,
  isEditing,
  onChangeField,
  onResetRecommended,
  isRecommended = true,
  optionsLocked = false,
  onEditCells,
  onSave,
  onCancel,
  saving,
  canSave,
  allowPersist = true,
  saveLabel,
  idPrefix = "org-payout",
  entries,
  onEntriesChange,
  entriesHint,
  onExport,
  exportColumnDisabled = false,
  exportingKey = "",
}) => {
  const [showRatioInfo, setShowRatioInfo] = useState(false);
  const shapeInfo = PAYOUT_SHAPES[shape] || PAYOUT_SHAPES.standard;
  const coveringPlaces = isRecommended ? EASY_PLACES : places;
  const coveringRatio = isRecommended
    ? `${DEFAULT_RATIO_NUMERATOR} in ${DEFAULT_RATIO_DENOMINATOR}`
    : `${ratioNumerator} in ${ratioDenominator}`;
  const coveringName = isRecommended ? "Standard" : shapeInfo.name;
  const capAt = firstCapStartsAtPlaces(ratioNumerator, ratioDenominator);
  const capApplies = Number(coveringPlaces) >= capAt;
  const prizePerEntry = planning?.prizePerEntry ?? "";
  const lastPlaceDollars = planning?.lastPlaceDollars ?? "";
  const roundToWholeDollars = planning?.roundToWholeDollars !== false;
  const planningPartial = isPayoutPlanningPartial(planning);

  return (
    <div className="card-board p-4 sm:p-6 space-y-4">
      <div>
        <h2 className="heading-display text-lg">
          {isRecommended ? "Recommended table" : "Custom table"}
        </h2>
        <p className="text-sm text-brand-muted mt-1">
          {coveringName} split covering {coveringPlaces} places at{" "}
          {coveringRatio}.
          {isRecommended
            ? " Tournaments pick the column that matches how many they pay."
            : null}
          {capApplies
            ? ` First place stays at or under 40% from the ${capAt}-place column on.`
            : null}
        </p>
      </div>

      {onEntriesChange ? (
        <div className="card-inset p-4 space-y-3">
          <div>
            <label
              htmlFor={`${idPrefix}-entries`}
              className="block text-sm font-medium text-brand-charcoal mb-1"
            >
              Entries
            </label>
            <input
              id={`${idPrefix}-entries`}
              type="number"
              min={0}
              value={entries}
              onChange={(e) => onEntriesChange(e.target.value)}
              className={`${inputClass} max-w-xs`}
              placeholder="Optional"
            />
          </div>
          {onExport ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <ExportFormats
                label="This column"
                scope="column"
                onExport={onExport}
                disabled={exportColumnDisabled}
                exportingKey={exportingKey}
              />
              <ExportFormats
                label="Full table"
                scope="full"
                onExport={onExport}
                exportingKey={exportingKey}
              />
            </div>
          ) : null}
          <p className="text-sm text-brand-muted">
            {entriesHint ||
              "Field size only highlights a column. It does not change the percentages."}
          </p>
        </div>
      ) : null}

      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <SecondaryButton
          onClick={onToggleMore}
          className="!px-3 !py-2 text-sm"
          aria-expanded={showMore}
        >
          {showMore ? "Hide options" : "More options"}
        </SecondaryButton>
        {canEdit && !isRecommended ? (
          <button
            type="button"
            onClick={onResetRecommended}
            className="inline-flex items-center justify-center min-h-11 px-3 text-sm font-display font-bold text-brand-charcoal hover:underline"
          >
            Reset to recommended
          </button>
        ) : null}
      </div>

      {showMore ? (
        <div className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6 items-start">
            <div className="space-y-4 min-w-0">
              <div className="relative overflow-hidden">
                <div
                  className={`space-y-4 ${
                    optionsLocked
                      ? "blur-sm pointer-events-none select-none"
                      : ""
                  }`}
                  aria-hidden={optionsLocked}
                >
                  <div className="space-y-4 card-inset p-4">
                    <div>
                      <label className="block text-sm font-medium text-brand-charcoal mb-1">
                        Places to cover
                      </label>
                      <input
                        type="number"
                        min={1}
                        max={50}
                        value={places}
                        disabled={!canEdit || optionsLocked}
                        onChange={(e) => onChangeField("places", e.target.value)}
                        className={inputClass}
                      />
                    </div>
                    <div>
                      <div className="flex items-center gap-1.5 mb-1">
                        <label
                        htmlFor={`${idPrefix}-ratio`}
                          className="text-sm font-medium text-brand-charcoal"
                        >
                          Payout ratio
                        </label>
                        <button
                          type="button"
                          className="text-brand-muted hover:text-brand-charcoal"
                          aria-expanded={showRatioInfo}
                          aria-controls="payout-ratio-info"
                          onClick={() => setShowRatioInfo((open) => !open)}
                        >
                          <FaInfoCircle className="h-3.5 w-3.5" />
                          <span className="sr-only">
                            How payout ratio affects this table
                          </span>
                        </button>
                      </div>
                      <div
                        id={`${idPrefix}-ratio`}
                        className={`flex items-center gap-2 px-3 py-2 border border-brand-charcoal/20 rounded-lg bg-white focus-within:ring-2 focus-within:ring-brand-gold-ink ${
                          !canEdit || optionsLocked ? "opacity-60" : ""
                        }`}
                      >
                        <input
                          type="number"
                          min={1}
                          value={ratioNumerator}
                          disabled={!canEdit || optionsLocked}
                          onChange={(e) =>
                            onChangeField("ratioNumerator", e.target.value)
                          }
                          className="w-14 border-none focus:outline-none bg-transparent font-score text-brand-charcoal"
                          aria-label="Payout ratio numerator"
                        />
                        <span className="text-sm text-brand-muted">in</span>
                        <input
                          type="number"
                          min={1}
                          value={ratioDenominator}
                          disabled={!canEdit || optionsLocked}
                          onChange={(e) =>
                            onChangeField("ratioDenominator", e.target.value)
                          }
                          className="w-14 border-none focus:outline-none bg-transparent font-score text-brand-charcoal"
                          aria-label="Payout ratio denominator"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-brand-charcoal mb-1">
                        Curve
                      </label>
                      <select
                        value={shape}
                        disabled={!canEdit || optionsLocked}
                        onChange={(e) => onChangeField("shape", e.target.value)}
                        className={inputClass}
                      >
                        {Object.values(PAYOUT_SHAPES).map((item) => (
                          <option key={item.id} value={item.id}>
                            {item.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {showRatioInfo ? (
                    <p
                      id="payout-ratio-info"
                      className="text-sm text-brand-muted card-inset p-3"
                    >
                      Tournaments use this ratio to pick a column. Field
                      size for a column is implied: places paid times the
                      second number, divided by the first. So 1 in 6 with
                      a 6-place column is 36 entries. USBC Rule 308 limits
                      first place to 40% of the prize fund when an event
                      has 100 or more entries. This table starts that cap
                      at the {capAt}-place column for {ratioNumerator} in{" "}
                      {ratioDenominator}. It does not check a live entry
                      count, and it does not enforce the rest of Rule 308:
                      a last-place prize at least equal to the prize fee,
                      one prize for each 10 entries, or second place at
                      least half of first.
                      {optionsLocked
                        ? ""
                        : " Changing these settings rebuilds the percentages."}
                    </p>
                  ) : null}

                  <div className="card-inset p-4 space-y-3">
                    <p className="text-sm font-medium text-brand-charcoal">
                      Prize money (optional)
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <DollarInput
                        id={`${idPrefix}-prize-per-entry`}
                        label="Prize per entry"
                        value={prizePerEntry}
                        disabled={!canEdit || optionsLocked}
                        onChange={(value) =>
                          onPlanningChange?.("prizePerEntry", value)
                        }
                      />
                      <DollarInput
                        id={`${idPrefix}-last-place`}
                        label="Last place floor"
                        value={lastPlaceDollars}
                        disabled={!canEdit || optionsLocked}
                        wholeDollars
                        onChange={(value) =>
                          onPlanningChange?.("lastPlaceDollars", value)
                        }
                      />
                    </div>
                    <p className="text-sm text-brand-muted">
                      Prize per entry fills in dollar amounts. Last place
                      floor is optional and only applies when prize per
                      entry is filled.
                      {roundToWholeDollars
                        ? " Payouts round to whole dollars. Leftover is paid out evenly to the top places until it is gone, skipping any place already at a cap."
                        : " Payouts keep cents and total the prize fund."}
                    </p>
                    {planningPartial ? (
                      <p className="text-sm text-amber-800">
                        Last place floor needs a prize per entry.
                      </p>
                    ) : null}
                  </div>
                </div>
                {optionsLocked ? (
                  <div
                    className="absolute inset-0 z-10 flex items-center justify-center p-4 bg-brand-cream/75"
                    role="status"
                  >
                    <div className="max-w-[18rem] text-center space-y-2">
                      <p className="text-sm text-brand-charcoal">
                        You edited the cells, so these options are locked.
                      </p>
                      {canEdit ? (
                        <button
                          type="button"
                          onClick={onResetRecommended}
                          className="inline-flex items-center justify-center min-h-11 px-3 text-sm font-display font-bold text-brand-charcoal hover:underline"
                        >
                          Reset to recommended
                        </button>
                      ) : null}
                    </div>
                  </div>
                ) : null}
              </div>

              <p className="text-sm text-brand-muted">{shapeInfo.blurb}</p>

              {canEdit ? (
                <div className="flex flex-wrap gap-2">
                  {optionsLocked || !isRecommended ? (
                    <SecondaryButton
                      onClick={onResetRecommended}
                      className="!px-3 !py-2 text-sm"
                    >
                      Reset to recommended
                    </SecondaryButton>
                  ) : null}
                  {!isEditing ? (
                    <SecondaryButton
                      onClick={onEditCells}
                      className="!px-3 !py-2 text-sm"
                    >
                      Edit cells
                    </SecondaryButton>
                  ) : (
                    <>
                      <PrimaryButton
                        onClick={onSave}
                        disabled={saving || !canSave}
                      >
                        {saving
                          ? "Saving..."
                          : saveLabel || (allowPersist ? "Save" : "Done")}
                      </PrimaryButton>
                      <SecondaryButton
                        onClick={onCancel}
                        className="!px-3 !py-2 text-sm"
                      >
                        Cancel
                      </SecondaryButton>
                    </>
                  )}
                </div>
              ) : null}
            </div>

            <div className="min-w-0">
              <PayoutCurveChart
                payoutData={payoutData}
                places={places}
                shape={shape}
                ratioNumerator={ratioNumerator}
                ratioDenominator={ratioDenominator}
              />
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default PayoutOptionsPanel;
